import { i18n } from '../i18n/nl';
import { stripTrailingSlash } from '../utils/url';
import type {
  Operator,
  QueueInfo,
  ServerMessage,
  ServiceStatus,
} from '../state/types';
import {
  ChatApiError,
  type ChatApiService,
  type ConversationState,
  type StartAnonymousInput,
  type StartAuthenticatedInput,
} from './service';

/**
 * HttpChatApiService — praat tegen de échte REST API van de chatservice
 * (`/v1/public/*`). Drop-in vervanger voor StubChatApiService: zelfde
 * ChatApiService-interface, dus de controller hoeft er niets van te weten.
 *
 * Zie docs/api.md voor het contract dat we zijn gaan volgen.
 *
 * Paden in deze service zijn RELATIEF aan `chatApiBaseUrl`. De full prefix
 * verschilt per deployment:
 *   - Stand-alone chat-service: `chatApiBaseUrl="https://chat.embrace.nl/v1/public"`
 *   - Umbraco-gehost (zoals `onlycustomers.umbraco13.stable-alpha`): `chatApiBaseUrl="https://.../chat"`
 * Beide zijn valide; de relatieve paden hieronder werken in beide gevallen.
 *
 * STATE OF KNOWLEDGE:
 *   - GET  /service/status                              → shape bekend, geverifieerd tegen staging
 *   - POST /conversations/requests/anonymous/new        → shape bekend (body), response = GUID-string
 *   - GET  /conversations/{id}                          → shape defensief geparst, nog niet
 *                                                         volledig geverifieerd (service offline)
 *   - POST /conversations/{id}/messages/new             → empty 200 OK
 */

export type HttpChatApiConfig = {
  /** Base URL, bv. "https://chat.embrace.nl" of "/chat" voor same-origin. */
  chatApiBaseUrl: string;

  /**
   * Openingstijden worden door onze backend (nog) niet geleverd. Host-site kan
   * ze hier meegeven zodat de closed-state ze kan tonen. Null = geen tabel.
   */
  openingHours?: ServiceStatus['openingHours'];

  /** Optioneel: overschrijf credentials-mode. Default 'same-origin'. */
  credentials?: RequestCredentials;

  /** Optioneel: fetch-timeout in ms (via AbortController). Default 10_000. */
  timeoutMs?: number;
};

export class HttpChatApiService implements ChatApiService {
  private readonly base: string;
  private readonly openingHours: ServiceStatus['openingHours'];
  private readonly credentials: RequestCredentials;
  private readonly timeoutMs: number;

  constructor(config: HttpChatApiConfig) {
    this.base = stripTrailingSlash(config.chatApiBaseUrl);
    this.openingHours = config.openingHours ?? null;
    this.credentials = config.credentials ?? 'same-origin';
    this.timeoutMs = config.timeoutMs ?? 10_000;
  }

  async fetchServiceStatus(): Promise<ServiceStatus> {
    const json = await this.fetchJson<{
      isAcceptingConversationRequests: boolean;
      showChatClientWhenOffline: boolean;
      maxMessageLength: number;
      turnstile: { siteKey?: string; verificationUrl?: string } | null;
    }>('GET', '/service/status');

    return {
      isAcceptingConversationRequests: Boolean(json.isAcceptingConversationRequests),
      showChatClientWhenOffline: Boolean(json.showChatClientWhenOffline),
      maxMessageLength: Number(json.maxMessageLength) || 4000,
      turnstile: json.turnstile?.siteKey
        ? { siteKey: json.turnstile.siteKey }
        : null,
      openingHours: this.openingHours,
      todayIndex: this.openingHours ? ((new Date().getDay() + 6) % 7) : null,
    };
  }

  async startAnonymous(input: StartAnonymousInput): Promise<string> {
    // Response is een plain-JSON-string (GUID). We gebruiken een raw helper
    // i.p.v. fetchJson zodat we de body als text kunnen parsen.
    const raw = await this.fetchText('POST', '/conversations/requests/anonymous/new', {
      firstName: input.firstName,
      lastName: input.lastName,
      emailAddress: input.emailAddress,
      initialQuestion: input.initialQuestion,
      captchaToken: input.captchaToken,
    });
    return parseGuid(raw, '/conversations/requests/anonymous/new');
  }

  async startAuthenticated(input: StartAuthenticatedInput): Promise<string> {
    // Server identificeert de bezoeker via de Bearer-token; body bevat
    // alleen de initiele vraag (zelfde shape als de oude chat-bundle).
    // Response is opnieuw een plain GUID-string.
    const raw = await this.fetchText(
      'POST',
      '/conversations/requests/authenticated/new',
      { initialQuestion: input.initialQuestion },
      { Authorization: `Bearer ${input.jwt}` },
    );
    return parseGuid(raw, '/conversations/requests/authenticated/new');
  }

  async getState(sessionId: string): Promise<ConversationState> {
    // De exacte response-shape is nog niet geverifieerd tegen de echte API.
    // We accepteren een flexibele vorm en loggen bij onverwachtte velden.
    const json = await this.fetchJson<unknown>(
      'GET',
      `/conversations/${encodeURIComponent(sessionId)}`,
    );

    return parseConversationState(json);
  }

  async sendMessage(sessionId: string, text: string): Promise<void> {
    await this.fetchJson<unknown>(
      'POST',
      `/conversations/${encodeURIComponent(sessionId)}/messages/new`,
      { messageText: text },
    );
  }

  async rateConversation(sessionId: string, rating: number): Promise<void> {
    await this.fetchJson<unknown>(
      'POST',
      `/conversations/${encodeURIComponent(sessionId)}/rate`,
      { rating },
    );
  }

  async endConversation(sessionId: string): Promise<void> {
    // Server: POST /conversations/{id}/leave → CustomerLeaveConversationRequest
    // → operator krijgt "klant verliet"-event. Geen body, lege 200 OK response.
    //
    // sendBeacon i.p.v. fetch zodat de call de unload-fase overleeft: als de
    // bezoeker direct na "Beëindig gesprek" de tab sluit, breekt fetch normaal
    // af terwijl beacon gegarandeerd verstuurd wordt door de browser. Fallback
    // naar fetch met keepalive: true wanneer beacon niet beschikbaar of geweigerd
    // (bv. iOS-Safari die soms POST-zonder-body als invalid beschouwt).
    const url = this.base + `/conversations/${encodeURIComponent(sessionId)}/leave`;
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // Lege Blob met text/plain ipv echt geen body — voorkomt CORS-preflight
      // (simple-request-criteria) én stelt sommige browsers tevreden over een
      // expliciete content-type.
      const sent = navigator.sendBeacon(url, new Blob([''], { type: 'text/plain' }));
      if (sent) return;
    }
    // credentials moet matchen met fetchJson zodat embedders met
    // 'include' (cross-origin cookie-auth) ook hier auth-cookies meesturen.
    await fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: this.credentials,
    });
  }

  // ==========================================================================
  // fetch wrappers
  // ==========================================================================

  private async fetchJson<T>(method: string, path: string, body?: unknown): Promise<T> {
    const raw = await this.fetchText(method, path, body);
    if (!raw) return {} as T;   // empty body → empty object (type-cast)
    try {
      return JSON.parse(raw) as T;
    } catch {
      throw new ChatApiError('unknown', `Invalid JSON from ${method} ${path}`);
    }
  }

  private async fetchText(
    method: string,
    path: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
  ): Promise<string> {
    const url = this.base + path;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers: {
          'Accept': 'application/json',
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(extraHeaders ?? {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: this.credentials,
        signal: controller.signal,
      });
    } catch (error) {
      clearTimeout(timer);
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ChatApiError('network', `Request timed out: ${method} ${path}`);
      }
      throw new ChatApiError('network', `Network error on ${method} ${path}`);
    }
    clearTimeout(timer);

    if (!response.ok) {
      throw await classifyHttpError(response, method, path);
    }

    // Safely read text-body (might be empty on 200 / 204)
    try {
      return await response.text();
    } catch {
      return '';
    }
  }
}

// ============================================================================
// Helpers
// ============================================================================


/**
 * Server geeft op `/anonymous/new` en `/authenticated/new` ofwel een naakte
 * GUID-string terug, ofwel een JSON-string ("\"abc\""). Trim + strip de
 * eventuele JSON-quotes voordat we 'm doorgeven aan de reducer.
 */
function parseGuid(raw: string, path: string): string {
  const guid = raw.trim().replace(/^"(.*)"$/, '$1');
  if (!guid) throw new ChatApiError('unknown', `Empty GUID from ${path}.`);
  return guid;
}

async function classifyHttpError(
  response: Response,
  method: string,
  path: string,
): Promise<ChatApiError> {
  const status = response.status;
  let bodyText = '';
  try {
    bodyText = await response.text();
  } catch {
    /* ignore */
  }
  const lower = bodyText.toLowerCase();

  if (status === 404) {
    return new ChatApiError('session_expired', `${method} ${path} returned 404`);
  }
  if (status === 400 && lower.includes('captcha')) {
    const kind = lower.includes('system error') ? 'captcha_system_error' : 'captcha_invalid';
    return new ChatApiError(kind, bodyText || 'Captcha validation failed');
  }
  if (status >= 500) {
    return new ChatApiError('service_unavailable', `${method} ${path} returned ${status}`);
  }
  return new ChatApiError(
    'unknown',
    `${method} ${path} returned ${status}: ${bodyText.slice(0, 200)}`,
  );
}

/**
 * Parse `WebCustomerConversationStateModel` (geverifieerde shape vanaf
 * onlycustomers.umbraco13.stable-alpha):
 *
 *   {
 *     kccConversationRequestId: string,
 *     pendingRequest: { rejected: bool, message: string } | null,    ← queue-fase
 *     conversation: {                                                 ← active-fase
 *       participants: [{ id, name, you: bool, avatarId, lastHeartBeat }, ...],
 *       messages: [{ id, participantId, timestamp, text, deletedOn }, ...],
 *       endedOn: ISO-string | null,                                   ← null = nog active
 *       rating: number | null,
 *     } | null,
 *     conversationEnded: { ... } | null,                              ← ended-fase
 *   }
 *
 * Mapping naar onze ConversationState:
 *   - pendingRequest aanwezig → queueInfo set, messages = []
 *   - conversation aanwezig → messages uit conversation.messages, operator
 *     uit conversation.participants (de !you), isEnded = endedOn !== null
 *   - conversationEnded aanwezig → isEnded = true (vergelijkbaar met conversation
 *     maar terminal)
 *
 * Auteur-bepaling per message: kijk welke participant de `participantId`
 * matcht en check `you=true` (klant) vs `you=false` (operator).
 *
 * Server biedt geen agentTyping → altijd false hier.
 */
function parseConversationState(raw: unknown): ConversationState {
  if (!raw || typeof raw !== 'object') {
    throw new ChatApiError('unknown', 'Conversation state response is not an object.');
  }
  const obj = raw as Record<string, unknown>;

  const pendingRequest = obj['pendingRequest'] as Record<string, unknown> | null | undefined;
  const conversation = obj['conversation'] as Record<string, unknown> | null | undefined;
  const conversationEnded = obj['conversationEnded'] as Record<string, unknown> | null | undefined;

  // Queue-fase. Special-case: server kan rejected=true meegeven (bv. "geen
  // medewerker beschikbaar") — dan eindigen we de sessie i.p.v. door te
  // pollen, en geven we de server-message als endMessage door.
  if (pendingRequest && typeof pendingRequest === 'object') {
    const rejected = Boolean(pendingRequest['rejected']);
    if (rejected) {
      const message =
        typeof pendingRequest['message'] === 'string'
          ? (pendingRequest['message'] as string)
          : i18n.misc.sessionRejectedFallback;
      return {
        messages: [],
        isEnded: true,
        operator: null,
        agentTyping: false,
        queueInfo: null,
        endMessage: message,
      };
    }
    return {
      messages: [],
      isEnded: false,
      operator: null,
      agentTyping: false,
      queueInfo: parseQueueInfoFromPending(pendingRequest),
    };
  }

  // Active of ended-fase
  const conv = conversation ?? conversationEnded;
  if (conv && typeof conv === 'object') {
    const participants = parseParticipants(conv['participants']);
    const operator = participants.find((p) => !p.you) ?? null;
    const messages = parseMessages(conv['messages'], participants);
    const endedOn = conv['endedOn'];
    const isEnded =
      Boolean(conversationEnded) ||
      (typeof endedOn === 'string' && endedOn.length > 0);

    return {
      messages,
      isEnded,
      operator: operator ? participantToOperator(operator) : null,
      agentTyping: false,                  // server biedt dit niet
      queueInfo: null,
    };
  }

  // Onbekende state (geen pending/conversation/ended) — leeg
  return {
    messages: [],
    isEnded: false,
    operator: null,
    agentTyping: false,
    queueInfo: null,
  };
}

type Participant = {
  id: string;
  name: string;
  you: boolean;
  avatarId: string | null;
};

function parseParticipants(raw: unknown): Participant[] {
  if (!Array.isArray(raw)) return [];
  const out: Participant[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const p = entry as Record<string, unknown>;
    const id = String(p['id'] ?? '');
    const name = String(p['name'] ?? '');
    const you = Boolean(p['you']);
    const avatarId = (p['avatarId'] as string | null) ?? null;
    if (!id) continue;
    out.push({ id, name, you, avatarId });
  }
  return out;
}

function parseMessages(raw: unknown, participants: Participant[]): ServerMessage[] {
  if (!Array.isArray(raw)) return [];
  const out: ServerMessage[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const m = entry as Record<string, unknown>;
    if (m['deletedOn']) continue;     // soft-deleted skippen

    const id = String(m['id'] ?? '');
    const text = String(m['text'] ?? '');
    const participantId = String(m['participantId'] ?? '');
    const ts = parseTimestamp(m['timestamp']);
    // Onparseerbare timestamps overslaan i.p.v. silent terugvallen op
    // Date.now(): zou ordering breken én de dedup-window-checks corrumpen.
    if (!id || !text || ts === null) continue;

    const sender = participants.find((p) => p.id === participantId);
    const author: ServerMessage['author'] =
      sender?.you === true ? 'customer' : sender ? 'operator' : 'system';

    out.push({ id, text, author, timestamp: ts });
  }
  return out;
}

function parseTimestamp(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw > 1e12 ? raw : raw * 1000;
  }
  if (typeof raw === 'string') {
    const n = Date.parse(raw);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

function participantToOperator(p: Participant): Operator {
  return {
    name: p.name,
    role: '',                            // server biedt geen role per participant
    initials: deriveInitials(p.name),
    avatarColor: '',                     // leeg → rendering valt terug op `var(--ec-accent)` (themable)
  };
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function parseQueueInfoFromPending(pending: Record<string, unknown>): QueueInfo | null {
  // De server geeft alleen een vrij-tekst-message en een rejected-flag terug.
  // We mappen naar { position, estimatedWaitMin } met defaults — de UI toont
  // nu vooral de animated progress-bar, niet specifieke cijfers.
  const rejected = Boolean(pending['rejected']);
  if (rejected) return null;
  return { position: 1, estimatedWaitMin: 0 };
}
